import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { authLogin, saveLoginResponse } from '../api/client';
import { consumePostLoginRedirect } from '../lib/auth';

interface LoginData {
  email:    string;
  password: string;
  error:    string;
  loading:  boolean;
}

/**
 * data-name 으로 받을 수 있는 편집 가능 필드 — type guard 패턴.
 *
 *   - HTML 의 data-name 오타 / 잘못된 필드 → 컴파일 + 런타임 차단
 *   - loading: boolean / error: string 같은 비입력 필드 보호
 */
type EditableField = 'email' | 'password';

function isEditableField(name: string): name is EditableField {
  return name === 'email' || name === 'password';
}

/**
 * 로그인 페이지 (P6 — Admin Login).
 *
 * Sola 패턴 (RfqCreateView 와 동일):
 *   - events 객체 매핑 (data-action + handleXxx)
 *   - 부분 갱신 — setError / setLoading (rerenderAll 회피 → input cursor 보호)
 *   - 에러 div 항상 그려두고 .hidden class 토글
 */
export class LoginView extends SolaView<LoginData> {
  override events = {
    click: {
      '@submit': 'handleSubmit',
    },
    input: {
      '@field': 'handleFieldChange',
    },
    change: {
      '@field': 'handleFieldChange',
    },
  };

  constructor() {
    super();
    this.data = {
      email:    '',
      password: '',
      error:    '',
      loading:  false,
    };
  }

  override getTitle(): string {
    return '로그인 — HCLTech 관리자';
  }

  // ─── 이벤트 핸들러 ───────────────────────────────────────────────
  handleFieldChange(_e: Event, target: HTMLElement): void {
    const name = target.getAttribute('data-name');
    if (!name || !isEditableField(name)) return;
    this.data[name] = (target as HTMLInputElement).value;
    // 입력 시 에러 자동 클리어
    if (this.data.error) {
      this.setError('');
    }
  }

  async handleSubmit(_e?: Event): Promise<void> {
    if (this.data.loading) return;

    const { email, password } = this.data;
    if (!email || !password) {
      this.setError('이메일과 비밀번호를 모두 입력하세요');
      return;
    }

    this.setError('');
    this.setLoading(true);

    try {
      const res = await authLogin(email, password);
      saveLoginResponse(res, email);

      const nextUrl = res.must_change_password
        ? '/change-password'
        : consumePostLoginRedirect();

      history.pushState(null, '', nextUrl);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (err) {
      this.setLoading(false);
      this.setError(err instanceof Error ? err.message : '로그인 실패');
    }
  }

  // ─── 부분 갱신 헬퍼 (RfqCreateView 패턴) ─────────────────────────
  private setError(msg: string): void {
    this.data.error = msg;
    const el = this._rootElement?.querySelector<HTMLElement>('.auth-error');
    if (!el) return;
    if (msg) {
      el.textContent = msg;
      el.classList.remove('hidden');
    } else {
      el.textContent = '';
      el.classList.add('hidden');
    }
  }

  private setLoading(on: boolean): void {
    this.data.loading = on;
    const root = this._rootElement;
    if (!root) return;
    const btn = root.querySelector<HTMLButtonElement>('[data-action="submit"]');
    if (btn) {
      btn.disabled = on;
      btn.textContent = on ? '로그인 중...' : '로그인';
    }
    const inputs = root.querySelectorAll<HTMLInputElement>('input[data-action="field"]');
    inputs.forEach((i) => { i.disabled = on; });
  }

  private escAttr(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  // ─── 렌더 ────────────────────────────────────────────────────────
  render(): HTMLElement {
    const { email, password, error, loading } = this.data;
    const disabled = loading ? 'disabled' : '';
    const errHidden = error ? '' : 'hidden';

    return htmlSolaWithInstance(this)`
      <div class="page page-auth">
        <div class="auth-card">
          <header class="auth-card-header">
            <h1>HCLTECH</h1>
            <p class="auth-subtitle">관리자 로그인</p>
          </header>

          <div class="auth-form">
            <div class="auth-field">
              <label for="auth-email">이메일</label>
              <input
                id="auth-email"
                type="email"
                autocomplete="email"
                data-action="field"
                data-name="email"
                value="${this.escAttr(email)}"
                ${disabled}
              />
            </div>

            <div class="auth-field">
              <label for="auth-password">비밀번호</label>
              <input
                id="auth-password"
                type="password"
                autocomplete="current-password"
                data-action="field"
                data-name="password"
                value="${this.escAttr(password)}"
                ${disabled}
              />
            </div>

            <div class="auth-error ${errHidden}">${this.escAttr(error)}</div>

            <button
              type="button"
              class="auth-submit"
              data-action="submit"
              ${disabled}
            >
              ${loading ? '로그인 중...' : '로그인'}
            </button>
          </div>

          <footer class="auth-footer">
            <p class="auth-hint">
              Slack 으로 자동 등록된 계정은 기본 비밀번호로 로그인 후 변경하세요.
            </p>
          </footer>
        </div>
      </div>
    `;
  }
}
